import { useMemo } from 'react';

import { SHELF_TEMPERATURES } from '@constants/index';
import { useMerchant } from '@contexts/operator/MerchantContext';
import operatorApiFetch from '@services/operator/apiService';
import productService from '@services/operator/productService';
import { productCategoryService } from '@services/operator/productCategoryService';

interface UseOperatorProductServiceOptions {
  merchantId?: string;
}

interface CheckSkuOptions {
  productId?: string;
  merchantId?: string;
}

/**
 * Operator Product Service Hook
 *
 * 包裝 productService，自動注入 merchantId 到需要的 API 呼叫中
 *
 * @param options - 選項
 * @param options.merchantId - Merchant ID（可選，如果未提供則從 MerchantContext 獲取）
 * @returns productService，需要 merchant_id 的方法會自動帶入
 *
 * @example
 * const service = useOperatorProductService({ merchantId: '123' });
 *
 * // merchant_id 會自動加入到需要的 request
 * await service.create({ name: 'Product A', sku: 'SKU-001' });
 * await service.checkSku('SKU-001');
 */
export function useOperatorProductService(options: UseOperatorProductServiceOptions = {}) {
  // useMerchant 仍是 .jsx，JSDoc `@returns {Object}` 讓 TS 推成 `Object`（無 property access），
  // 這裡顯式 narrow 到實際 runtime 結構，僅取本 hook 用得到的 merchantId。
  const merchantContext = useMerchant() as { merchantId?: string | null } | null | undefined;
  const contextMerchantId = merchantContext?.merchantId ?? undefined;

  const merchantId = options.merchantId ?? contextMerchantId;  // 傳入優先，否則用 context

  return useMemo(() => ({
    // ========== 直接委託給 productService（不需要 merchantId）==========
    get: productService.get,
    getIds: productService.getIds,
    batchUpdate: productService.batchUpdate,
    generateLabel: productService.generateLabel,
    getProductAttributeDefinitions: productService.getProductAttributeDefinitions,
    previewReplaceSku: productService.previewReplaceSku,
    replaceSku: productService.replaceSku,
    export: productService.export,
    import: productService.import,

    // ========== 需要注入 merchantId 的方法 ==========

    // 獲取產品列表（用於搜尋）- params.merchant_id 優先，否則用 hook 的 merchantId
    index: async (params: Record<string, unknown> = {}) => {
      return productService.fetchList({ merchant_id: merchantId, ...params });
    },

    // fetchList 別名 - params.merchant_id 優先，否則用 hook 的 merchantId
    fetchList: async (params: Record<string, unknown> = {}) => {
      return productService.fetchList({ merchant_id: merchantId, ...params });
    },

    // 獲取單個產品（別名）
    getById: productService.get,

    // 獲取產品列表（用於表格）- 注入 merchant_id
    getRecords: async (params: Record<string, unknown> = {}) => {
      return productService.getRecords({ ...params, merchant_id: merchantId });
    },

    // 創建產品 - 注入 merchant_id
    create: async (data: Record<string, unknown>) => {
      const res = await operatorApiFetch('products', {
        method: 'POST',
        body: {
          merchant_id: merchantId,
          product: { ...data }
        }
      });
      return res.json();
    },

    // 更新產品
    update: productService.update,

    // 刪除產品
    delete: async (id: string) => {
      const res = await operatorApiFetch(`products/${id}`, {
        method: 'DELETE'
      });
      return res.json();
    },

    // 獲取序號
    getSerialNumbers: async (productId: string, params: Record<string, unknown> = {}) => {
      const res = await operatorApiFetch(`products/${productId}/serial_numbers/get_records`, {
        method: 'POST',
        body: { ...params }
      });
      return res.json();
    },

    // 檢查品號 - 包裝 productService.checkSku，fallback 到 hook 的 merchantId
    checkSku: async (sku: string, checkOptions: CheckSkuOptions = {}) => {
      // 如果沒有傳 productId 也沒有傳 merchantId，使用 hook 的 merchantId
      const finalOptions: CheckSkuOptions = { ...checkOptions };
      if (!finalOptions.productId && !finalOptions.merchantId && merchantId) {
        finalOptions.merchantId = merchantId;
      }
      return productService.checkSku(sku, finalOptions);
    },

    // 獲取品牌列表（多貨主場景：自動以 hook 的 merchantId 過濾，避免跨貨主品牌混入下拉）
    getBrands: async () => {
      const brands = await productService.getBrands(
        merchantId ? { merchantId } : {}
      );
      // productService 返回的格式已經包含 label/value，直接返回
      return brands;
    },

    // 獲取溫層選項（從 constants 獲取）
    getTemperatures: async () => {
      return SHELF_TEMPERATURES;
    },

    // 獲取分類選項（organization 層級，不需 merchantId）
    getCategories: async () => {
      const data = await productCategoryService.fetchList();
      if (data.success && Array.isArray(data.data)) {
        return data.data.map((c: { name: string; id: string }) => ({ label: c.name, value: c.id }));
      }
      return [];
    },

    // 下載匯入模板 - 注入 merchant_id
    downloadImportTemplate: async (locale: string) => {
      return productService.downloadImportTemplate(locale, merchantId);
    }
  }), [merchantId]);
}
